import React, { useContext, useState, useEffect, useCallback, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { usersApi } from "../services/users";
import LiveStatusBadge from "./LiveStatusBadge";
import OfflineConfirmModal from "./OfflineConfirmModal";
import {
  setAcceptingStatus,
  subscribeAcceptingStatus,
  seedAcceptingStatusFromFetch,
} from "../lib/acceptingStatus";
import {
  subscribeLiveControlVisible,
  seedLiveControlVisibleFromFetch,
} from "../lib/liveControlVisibility";
import "./LiveStatusFloat.css";

// Floating Live/Offline pill — pinned to the TOP-RIGHT corner of the main app
// pages (never in the header). Opt-in: shows only when the talent enabled the
// control in Preferences, OR whenever they're Offline (a safety so an Offline
// talent always has a one-tap way back to Live). This owns the fetch / toggle /
// confirm flow that used to live in the Header + the Requests page.
const LiveStatusFloat: React.FC = () => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const { pathname } = useLocation();

  const [isAccepting, setIsAccepting] = useState(true);
  const [optIn, setOptIn] = useState(false);
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  // Seed both shared stores from the talent's record (only if nothing fresher
  // has happened yet). Subscribers below drive local state.
  useEffect(() => {
    if (!isAuthenticated || !user?.slug) return;
    usersApi
      .get(user.slug)
      .then((r) => {
        seedAcceptingStatusFromFetch(r.isAcceptingRequests !== false);
        seedLiveControlVisibleFromFetch(r.showLiveToggle === true);
      })
      .catch(() => {});
  }, [isAuthenticated, user?.slug]);

  useEffect(() => subscribeAcceptingStatus(setIsAccepting), []);
  useEffect(() => subscribeLiveControlVisible(setOptIn), []);

  const toggleStatus = useCallback(
    async (accepting: boolean) => {
      if (!user?.slug) return;
      setStatusLoading(true);
      try {
        const newVal = await setAcceptingStatus(user.slug, accepting);
        setIsAccepting(newVal);
        setShowOfflineModal(false);
      } catch (err) {
        console.error("Failed to update status", err);
      } finally {
        setStatusLoading(false);
      }
    },
    [user?.slug]
  );

  const requestToggle = () => {
    if (statusLoading) return;
    if (isAccepting) setShowOfflineModal(true);
    else toggleStatus(true);
  };

  // Only on the talent's own app pages (the four tabs). Kept off content /
  // checkout / public surfaces so a floating "Live" pill never appears where it
  // doesn't belong.
  const onAppPage =
    pathname === "/requests" ||
    pathname === "/balance" ||
    pathname === "/profile" ||
    pathname.startsWith("/qr/");

  const show = isAuthenticated && onAppPage && (optIn || !isAccepting);

  // The pill's real height, for whatever must start below it (the "Wasn't
  // played?" row on Requests: ClaimAlertBanner.css). Measured, not guessed,
  // so a larger text size or another language never overlaps it.
  const floatRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = floatRef.current;
    if (!show || !el) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty("--live-float-h", `${Math.ceil(el.getBoundingClientRect().height)}px`);
    publish();
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(publish) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      root.style.removeProperty("--live-float-h");
    };
  }, [show]);

  if (!show) return null;

  return (
    <>
      <div className="live-status-float" ref={floatRef}>
        <LiveStatusBadge isAccepting={isAccepting} onClick={requestToggle} />
      </div>
      <OfflineConfirmModal
        open={showOfflineModal}
        onClose={() => setShowOfflineModal(false)}
        onConfirm={() => toggleStatus(false)}
        loading={statusLoading}
      />
    </>
  );
};

export default LiveStatusFloat;
